import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../../config/api';
import { FaLock, FaEye, FaEyeSlash, FaEnvelope, FaUser } from 'react-icons/fa';
import cloudsafeLogo from '../../../../assets/cloudsafe-logo.png';
import '../ResetPassword/ResetPassword.css';

/**
 * Invite / referral acceptance page (route: /join/:token). A staff member invited
 * to a contractor company lands here, sets their name + password, and is activated
 * straight into that company's team. Distinct from the password-reset page.
 */
const InvitePage = () => {
  const [pw, setPw] = useState({ password: '', confirmPassword: '' });
  const [name, setName] = useState({ firstName: '', lastName: '' });
  const [show, setShow] = useState({ password: false, confirmPassword: false });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [ctx, setCtx] = useState(null); // { email, companyName }
  const navigate = useNavigate();
  const { token } = useParams();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(getApiEndpoint(`/api/auth/validate-reset-token/${token}`));
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (res.ok && data.valid) {
          setCtx({ email: data.email || '', companyName: data.companyName || null });
        } else {
          toast.error(data.message || 'This invitation is invalid or has expired.');
          navigate('/login');
        }
      } catch {
        if (alive) { toast.error('Could not validate this invitation.'); navigate('/login'); }
      } finally { if (alive) setChecking(false); }
    })();
    return () => { alive = false; };
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.password !== pw.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (pw.password.length < 8) { toast.error('Password must be at least 8 characters long'); return; }
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: pw.password, firstName: name.firstName, lastName: name.lastName }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || 'Failed to set up your account'); }
      toast.success('Account set up — you can now sign in.');
      navigate('/login');
    } catch (error) { toast.error(error.message || 'Failed to set up your account'); }
    finally { setLoading(false); }
  };

  if (checking) {
    return <div className="rp-page"><div className="rp-card"><p style={{ color: '#6b7280', textAlign: 'center', margin: 0 }}>Checking your invitation…</p></div></div>;
  }

  return (
    <div className="rp-page">
      <div className="rp-card">
        <div className="rp-brand"><img src={cloudsafeLogo} alt="CloudSafe" className="rp-logo" /></div>

        <div className="rp-invite-banner">
          <i className="fa-solid fa-user-plus" />
          You've been invited to join{ctx?.companyName ? <>&nbsp;<strong>{ctx.companyName}</strong></> : ' the team'}
        </div>

        <h1 className="rp-title">Accept your invite</h1>
        <p className="rp-subtitle">
          Set up your account to join{ctx?.companyName ? ` ${ctx.companyName}` : ' the team'} on CloudSafe. You'll be connected to their workspace straight away.
        </p>

        <form onSubmit={submit}>
          {ctx?.email && (
            <div className="rp-group">
              <label className="rp-label">Your email</label>
              <div className="rp-input-wrap">
                <FaEnvelope className="rp-input-icon" />
                <input type="email" className="rp-input" value={ctx.email} readOnly disabled />
              </div>
            </div>
          )}

          <div className="rp-row">
            <div className="rp-group">
              <label className="rp-label">First name</label>
              <div className="rp-input-wrap">
                <FaUser className="rp-input-icon" />
                <input type="text" className="rp-input" placeholder="First name" value={name.firstName}
                  onChange={(e) => setName((n) => ({ ...n, firstName: e.target.value }))} autoFocus />
              </div>
            </div>
            <div className="rp-group">
              <label className="rp-label">Last name</label>
              <div className="rp-input-wrap">
                <FaUser className="rp-input-icon" />
                <input type="text" className="rp-input" placeholder="Last name" value={name.lastName}
                  onChange={(e) => setName((n) => ({ ...n, lastName: e.target.value }))} />
              </div>
            </div>
          </div>

          <div className="rp-group">
            <label className="rp-label">Create a password</label>
            <div className="rp-input-wrap">
              <FaLock className="rp-input-icon" />
              <input type={show.password ? 'text' : 'password'} className="rp-input" placeholder="Enter a password"
                value={pw.password} onChange={(e) => setPw((p) => ({ ...p, password: e.target.value }))} required />
              <button type="button" className="rp-toggle" tabIndex={-1} onClick={() => setShow((s) => ({ ...s, password: !s.password }))}>
                {show.password ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="rp-group">
            <label className="rp-label">Confirm password</label>
            <div className="rp-input-wrap">
              <FaLock className="rp-input-icon" />
              <input type={show.confirmPassword ? 'text' : 'password'} className="rp-input" placeholder="Re-enter the password"
                value={pw.confirmPassword} onChange={(e) => setPw((p) => ({ ...p, confirmPassword: e.target.value }))} required />
              <button type="button" className="rp-toggle" tabIndex={-1} onClick={() => setShow((s) => ({ ...s, confirmPassword: !s.confirmPassword }))}>
                {show.confirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <p className="rp-hint">Use at least 8 characters. A mix of letters, numbers and a symbol is strongest.</p>

          <button type="submit" className="rp-submit" disabled={loading}>
            {loading ? 'Setting up…' : 'Create account & join →'}
          </button>
        </form>

        <div className="rp-foot"><span>Already have an account?</span> <Link to="/login" className="rp-link">Sign in</Link></div>
      </div>
      <p className="rp-powered">🛡️ Powered by CloudSafe</p>
    </div>
  );
};

export default InvitePage;
